import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import ApplicantShell from "../components/ApplicantShell";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import config from "../config/config";
import { clearStoredAuth } from "../utils/authSession";

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchJson(path) {
  const response = await fetch(`${config.API_BASE_URL}${path}`, { headers: getAuthHeaders() });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.detail || `Request failed (${response.status})`);
    error.status = response.status;
    error.detail = data?.detail || "";
    throw error;
  }
  return data;
}

function formatDate(value) {
  if (!value) return "gerade";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("de-DE", { dateStyle: "medium" });
}

export default function ApplicantMatches() {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");

  useEffect(() => {
    document.title = "Experts4Europe | Matches";
  }, []);

  const handleAuthError = (err) => {
    if (Number(err?.status) !== 401) return false;
    clearStoredAuth();
    const detail = String(err?.detail || err?.message || "").toLowerCase();
    const isTimeout = detail.includes("expired") || detail.includes("inactivity") || detail.includes("session");
    setSessionExpiredMessage(
      isTimeout
        ? "Du wurdest wegen Inaktivitaet automatisch abgemeldet. Bitte melde dich erneut an."
        : "Deine Anmeldung ist nicht mehr gueltig. Bitte melde dich erneut an."
    );
    setSessionExpiredOpen(true);
    return true;
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchJson("/api/applicant-dashboard/mission-control");
        if (mounted) setSnapshot(data);
      } catch (err) {
        if (handleAuthError(err)) return;
        if (mounted) setError(err.message || "Die Chancen konnten nicht geladen werden.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const opportunities = snapshot?.opportunities || [];
  const featuredOpportunity = opportunities[0] || null;
  const otherOpportunities = opportunities.slice(1);
  const searchItems = useMemo(() => ([
    { href: "/dashboard/applicant", label: "Dashboard", description: "Zur Mission Control" },
    { href: "/dashboard/applicant/matches", label: "Matches", description: "Deine neuen Chancen" },
  ]), []);

  return (
    <ApplicantShell
      title="Matches"
      subtitle="Hier pruefst du nur neue Chancen und ihre Passung zu deinem Profil."
      actions={(
        <>
          <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/dashboard/applicant")}>Dashboard</Button>
          <Button className="applicant-cta-button" onClick={() => navigate("/stb/job-marketplace")}>Zum Marktplatz</Button>
        </>
      )}
      notifications={[]}
      searchItems={searchItems}
    >
      <div className="applicant-workspace-page">
        {error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{error}</div> : null}
        <section className="applicant-workspace-hero applicant-workspace-hero--matches">
          <div className="applicant-workspace-hero__main">
            <span className="applicant-section-kicker">Neue Chancen</span>
            <h2>Welche Stellen jetzt gut zu dir passen</h2>
            <p>Du siehst hier neue Jobs, die zu deinem Profil, deinem Ort und deinem aktuellen Stand passen.</p>
          </div>
        </section>

        <div className="applicant-workspace-grid applicant-workspace-grid--main">
          <section className="applicant-workspace-card">
            <div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Lead-Chance</span><h3>Deine staerkste neue Chance</h3></div></div>
            {loading ? <div className="applicant-empty-card">Chancen werden geladen ...</div> : featuredOpportunity ? (
              <article className="applicant-opportunity-feature">
                <div className="applicant-opportunity-feature__meta"><span className="applicant-opportunity-card__fit">{featuredOpportunity.fit_band}</span><small>{formatDate(featuredOpportunity.released_at)}</small></div>
                <strong>{featuredOpportunity.title}</strong>
                <p>{featuredOpportunity.employer_name} · {featuredOpportunity.region_label}</p>
                <div className="applicant-opportunity-feature__stats">
                  <div><span>Fit</span><strong>{featuredOpportunity.fit_score}</strong></div>
                  <div><span>Beruf</span><strong>{featuredOpportunity.profession || "Noch offen"}</strong></div>
                </div>
                <button type="button" className="applicant-inline-link" onClick={() => navigate(featuredOpportunity.href)}><span>Chance oeffnen</span><ChevronRight size={15} /></button>
              </article>
            ) : <div className="applicant-empty-card">Aktuell liegt keine neue Chance ausserhalb deiner laufenden Bewerbungen offen.</div>}
          </section>

          <section className="applicant-workspace-card">
            <div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Mehr Chancen</span><h3>Weitere passende Stellen</h3></div></div>
            <div className="applicant-opportunity-stack">
              {otherOpportunities.length ? otherOpportunities.map((item) => (
                <article key={item.job_id} className="applicant-opportunity-card">
                  <div className="applicant-opportunity-card__meta"><span className="applicant-opportunity-card__fit">{item.fit_band}</span><small>{formatDate(item.released_at)}</small></div>
                  <strong>{item.title}</strong>
                  <p>{item.employer_name} · {item.region_label}</p>
                  <div className="applicant-opportunity-card__foot"><span>Fit {item.fit_score}</span><button type="button" className="applicant-inline-link" onClick={() => navigate(item.href)}><span>Zur Chance</span><ChevronRight size={15} /></button></div>
                </article>
              )) : <div className="applicant-empty-card">Keine weiteren neuen Chancen sichtbar.</div>}
            </div>
          </section>
        </div>
      </div>

      <Dialog open={sessionExpiredOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Sitzung abgelaufen</DialogTitle>
            <DialogDescription>{sessionExpiredMessage}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/")}>Startseite</Button>
            <Button className="applicant-cta-button" onClick={() => navigate("/login", { replace: true })}>Erneut anmelden</Button>
          </div>
        </DialogContent>
      </Dialog>
    </ApplicantShell>
  );
}
